import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
  useTicket, useTicketCaseFile, useUpdateTicketStatus, useAssignTicket, useAddTicketNote, useComposeDraft,
  useSetTicketCustomer, useSetTicketProduct, useAssignees, useRecomputeTicket,
  useSplitProducts, useMergeDuplicate, useNotDuplicate, useSetTicketRequest, useSetTicketProducts,
} from '../hooks/useTicketsApi';
import { useTicketAuditTrail } from '../hooks/useAuditApi';
import { useToast } from '../components/ui/Toast';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { NameWithCode, HubspotChip, BcLink } from '../components/ui/EntityLinks';
import { customerState, isUnsettled, productState } from '../lib/entity-state';
import {
  SetCustomerPanel, SetProductPanel, SplitProductsPanel, SplitRelationsPanel, DuplicatePanel, RelatedLanguagePanel,
  ReviewReasonGuide, ReplyLanguagePanel, RequestFactsPanel, consequenceSentence,
  type FactChoice, type RequestFactsEdit,
} from '../components/TicketEntityPanels';
import AssignPanel from '../components/AssignPanel';
import ReviewQueueNav, { isReviewOrigin } from '../components/ReviewQueueNav';
import { ReviewReasonChips } from '../components/TicketTable';
import { ReplyCard } from '../components/ReplyCard';
import { CaseFileCard } from '../components/TicketCaseFile';
import { CorrectionPanel, CustomerHistoryPanel, DeliveryProofPanel } from '../components/TicketDeliveryPanels';
import { TicketDetailsCard, TicketHistoryCard } from '../components/TicketDetailsRail';
import { useAuth } from '../contexts/AuthContext';
import {
  CLOSE_REASONS, CLOSE_REASON_LABELS, STATUS_SIDE_EFFECTS,
  TICKET_STATUSES, TICKET_STATUS_LABELS, TEST_DATA_LABEL,
  allReviewReasons, labelFor, reviewReasonsBesideStatus,
  ticketStatusGlyph, ticketStatusLabel, ticketStatusTone, ticketStatusTooltip, tooltipFor,
} from '../lib/labels';
import { Button } from '../components/ui/Button';
import { Badge, StatusPill } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Field, Select } from '../components/ui/Field';
import { LoadError } from '../components/ui/LoadError';
import { LoadingAnnouncement, SkeletonCards } from '../components/ui/Skeleton';
import { isoDate } from '../lib/dates';
import type {
  ComposeDraftResult, CustomerCandidate,
  ProductCandidate, PurchasedFamily, RecomputeResult, SetRequestResult, SplitProductsResult, Ticket,
  WithdrawalOutcome,
} from '../types/domain';

/**
 * The two terminal states, and the rule that guards them (P22, decision 6).
 * A ticket whose reply has already been SENT is not dragged back into open
 * work by a dropdown — `update-status.ts` answers 409, and this screen does not
 * offer the move in the first place. Reopening it happens through the
 * correction, which is the panel below and the only door back in (P24).
 */
const TERMINAL_STATUSES = new Set(['Closed', 'Cancelled']);

const CASE_FILE_KINDS = new Set(['Communication', 'Attachment', 'CaseFileEntry']);

/** The review reason a C9 DOCUMENT override answers — set by step 9, cleared by nothing. */
export const LANGUAGE_REVIEW_REASON = 'DraftDocumentLanguageUnavailable';

/**
 * The review reasons the P23 English REPLY override answers.
 *
 * Mirrors `REPLY_LANGUAGE_REVIEW_REASONS` in `api/src/agent/steps/compose-draft.ts`
 * — the server's gate accepts exactly these two under `allowEnglishReply`, so
 * the screen must offer the panel for exactly these two. Both mean the same
 * thing from the mail's side: there is no wording we can send this person.
 */
export const REPLY_LANGUAGE_REVIEW_REASONS = ['ReplyLanguageUnavailable', 'DraftNoTemplate'];

/** Ticket states that can never receive a draft. The endpoint answers 409 on these. */
const UNCOMPOSABLE = new Set(['Closed', 'Cancelled']);

// The two reason lists moved to `lib/entity-state.ts` in P30, unchanged: the
// boards now ask the same question once per row, and a second copy of the rule
// would let /tickets mark a ticket "not identified" while the ticket itself
// offers no panel to identify it.

/**
 * Roman's decision 3 — the hold (P21) and, since P27, the two decisions that
 * answer it. The panel is NOT gated on this reason: a pair the agent never
 * caught (two mails in one tick, or a fact corrected afterwards) carries no
 * reason at all and still has to be decidable. The reason only decides whether
 * the ticket is HELD.
 */
const DUPLICATE_REVIEW_REASON = 'PotentialDuplicate';

/**
 * Roman's decision 4 (P26) — "the SDS of everything we buy".
 *
 * Deliberately NOT in `PRODUCT_REVIEW_REASONS` above: both are "which article",
 * but the control that answers them is a different one. Showing the catalogue
 * search here would invite somebody to settle ONE article on a request that
 * asked for all of them — which the server allows, and which is exactly the
 * narrowing a person should have to mean.
 */
const PRODUCT_SCOPE_REVIEW_REASON = 'ProductScopeAllPurchased';

/** `ReviewReason` is an appended `; `-joined string, not a single value. */
export function reviewReasons(reviewReason: string | null): string[] {
  return (reviewReason ?? '').split(';').map(r => r.trim()).filter(Boolean);
}

/**
 * Turns one compose outcome into one honest sentence.
 *
 * "Drafted" and "the step ran and declined" are different things, and a
 * Regulatory user who believes a draft is waiting when the ticket actually went
 * back into their own queue is the failure this screen exists to prevent. A
 * skip is reported as a skip, with the step's own explanation.
 */
function composeToast(result: ComposeDraftResult): { text: string; kind: 'success' | 'info' | 'error' } {
  if (result.outcome === 'Drafted') {
    return { text: `Draft ${result.draftId} composed — it is now in Approvals`, kind: 'success' };
  }
  if (result.outcome === 'Blocked') {
    return {
      text: `No draft: the template still has unfilled placeholders (${(result.unfilledPlaceholders ?? []).join(', ')}). The ticket is with Regulatory.`,
      kind: 'error',
    };
  }
  return { text: `No draft — ${result.reason}. ${result.explanation}`, kind: 'info' };
}

/**
 * Scrolls the open fact editor into view and puts the caret in its first field.
 *
 * `block: 'nearest'` and not `'center'`: the panel sits directly under the
 * facts card, so on an ordinary window it is already visible and the page must
 * not jump for a click that changed nothing. The focus target is deliberately
 * restricted to fields — the first focusable element in these panels is the
 * "Cancel" link, and landing the keyboard on the way out is worse than not
 * moving it at all.
 */
function revealPanel(node: HTMLElement | null) {
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  node.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true });
}

export default function TicketDetail() {
  const { ticketNumber } = useParams<{ ticketNumber: string }>();
  // Where the reader came from, handed over by the row click (TicketTable's
  // `origin`). Router state and nothing else: it dies on a reload, which is
  // correct — the queue somebody was walking is not a property of this ticket.
  const origin = (useLocation().state as { origin?: unknown } | null)?.origin;
  const { canEdit } = usePagePermissions('/tickets');
  const { toast } = useToast();
  // P39: the Case File / Audit tab pair is gone. The ledger moved into the right
  // rail (`TicketHistoryCard`), because it is what a reader keeps an eye on while
  // doing something else; the case file stayed in the work column, because adding
  // to it is an action. Neither is behind a tab any more, so neither is hidden.
  const [noteTitle, setNoteTitle] = useState('');
  const [noteDetail, setNoteDetail] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [closeReason, setCloseReason] = useState('');

  const [expandedAuditId, setExpandedAuditId] = useState<string | number | null>(null);
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [confirmEnglishReply, setConfirmEnglishReply] = useState(false);
  const [pendingCustomer, setPendingCustomer] = useState<FactChoice<CustomerCandidate> | null>(null);
  const [pendingProduct, setPendingProduct] = useState<FactChoice<ProductCandidate> | null>(null);
  /**
   * P29 — several articles ticked at once. Its own piece of state and not a
   * one-element special case of `pendingProduct`: the confirmation it needs is
   * a different sentence (N tickets, N mails) and the mutation it runs is a
   * different endpoint.
   */
  const [pendingProducts, setPendingProducts] = useState<FactChoice<ProductCandidate[]> | null>(null);
  /**
   * P28 — which settled fact a person has opened for editing.
   *
   * One piece of state and not three booleans: two panels open at once would
   * put two ConfirmModals one click apart, and a person who has just changed
   * the customer should see what THAT did before changing anything else.
   */
  const [editingFact, setEditingFact] = useState<'customer' | 'product' | 'request' | null>(null);
  /**
   * The slot the open editor renders into — immediately under the facts card,
   * so a panel appears where the "Change" link was pressed.
   *
   * Roman, 2026-09-03: *"die änderungsmöglichkeiten sind viel zu weit unten und
   * weg von dem ort von dem man geklickt hat, manchmal sieht man es nicht
   * einmal."* The panel used to render below the delivery history, the language
   * panel and the whole review guide — on `DRA-2026-00048` that is roughly 900
   * pixels down, with the Save button off the bottom of the screen. A control
   * a person has to go looking for is a control they do not know appeared.
   */
  const factEditorRef = useRef<HTMLDivElement>(null);
  // Bringing it into view is not a substitute for placing it well; it is what
  // makes the placement hold on a short window, where even the facts card can
  // push the panel past the fold.
  useEffect(() => {
    if (editingFact) revealPanel(factEditorRef.current);
  }, [editingFact]);
  /** A language edit waiting for its confirmation — the only request field that is signed for. */
  const [pendingRequest, setPendingRequest] = useState<RequestFactsEdit | null>(null);
  const [pendingSplit, setPendingSplit] = useState<{ productIds: string[]; families: PurchasedFamily[] } | null>(null);
  // P27. Both duplicate decisions go through a ConfirmModal: one closes a
  // ticket, the other releases a request into the authorization ladder and the
  // draft lane. Neither is a click somebody should be able to make by accident.
  const [pendingDuplicateAction, setPendingDuplicateAction] = useState<'merge' | 'notDuplicate' | null>(null);

  const { data: ticket, isLoading, isError, error, errorUpdatedAt, refetch } = useTicket(ticketNumber);
  const { data: caseFile } = useTicketCaseFile(ticketNumber);
  const { data: auditPage } = useTicketAuditTrail(ticketNumber);
  const updateStatus = useUpdateTicketStatus(ticketNumber ?? '');
  const assign = useAssignTicket(ticketNumber ?? '');
  const recompute = useRecomputeTicket(ticketNumber ?? '');
  const { data: assignees } = useAssignees();
  const { user } = useAuth();
  const addNote = useAddTicketNote(ticketNumber ?? '');
  const compose = useComposeDraft(ticketNumber ?? '');
  const setCustomer = useSetTicketCustomer(ticketNumber ?? '');
  const setProduct = useSetTicketProduct(ticketNumber ?? '');
  const setRequest = useSetTicketRequest(ticketNumber ?? '');
  const splitProducts = useSplitProducts(ticketNumber ?? '');
  const setProducts = useSetTicketProducts(ticketNumber ?? '');
  const mergeDuplicate = useMergeDuplicate(ticketNumber ?? '');
  const notDuplicate = useNotDuplicate(ticketNumber ?? '');

  /**
   * One sentence for what the recompute actually did.
   *
   * The whole point of P21 is that setting a fact is only the first half; a
   * user who sees "Customer set" and nothing else has learned nothing about
   * whether the ticket moved. So the toast names the outcome of the chain — the
   * draft, the Sales Manager who was asked, the duplicate that held it, or the
   * reasons still standing — and it is sticky, because it is the answer.
   */
  /**
   * What an edit WITHDREW, as the first half of the sentence (P28).
   *
   * Reported and not implied: a person who has just watched a draft disappear
   * from Approvals has to be told it was this click that did it, and told which
   * draft — the ledger says so too, but nobody reads the ledger to find out
   * what happened two seconds ago.
   */
  function withdrawalSentence(invalidated: WithdrawalOutcome | undefined): string {
    if (!invalidated) return '';
    const parts: string[] = [];
    if (invalidated.drafts.length > 0) {
      parts.push(
        `draft ${invalidated.drafts.map(d => d.draftId).join(', ')} withdrawn`
        + (invalidated.drafts.some(d => d.previousApprovedBy) ? ' (it had been approved)' : ''),
      );
    }
    if (invalidated.authorizationRequests.length > 0) {
      parts.push(
        `the request to ${invalidated.authorizationRequests.map(r => r.requestedFrom ?? 'a Sales Manager').join(', ')} cancelled`,
      );
    }
    return parts.length === 0 ? '' : `${parts.join(', ')}. `;
  }

  //
  // The parameter is the SHAPE this function reads and not one named response
  // type: `not-duplicate` produces the same recompute result and withdraws
  // nothing, and widening it to `SetEntityResult` would force an `invalidated`
  // field onto an endpoint that has no business having one.
  function recomputeToast(
    result: { recompute: RecomputeResult; invalidated?: WithdrawalOutcome },
  ): { text: string; kind: 'success' | 'info' | 'error' } {
    const r = result.recompute;
    const withdrew = withdrawalSentence(result.invalidated);
    if (r.heldAsDuplicateOf) {
      return {
        text: `Set. ${withdrew}But ${r.heldAsDuplicateOf.ticketNumber} (${r.heldAsDuplicateOf.status}) already covers `
          + 'this exact request, so this ticket is held in review as a probable duplicate. Nothing was drafted.',
        kind: 'info',
      };
    }
    if (r.compose?.outcome === 'Drafted') {
      return { text: `Set. ${withdrew}Draft ${r.compose.draftId} is now in Approvals.`, kind: 'success' };
    }
    if (r.authorization?.requestedFrom) {
      return {
        text: `Set. ${withdrew}The purchase check found nothing, so ${r.authorization.requestedFrom} was asked to `
          + 'authorise it. It is on Authorizations now.',
        kind: 'info',
      };
    }
    if (r.remainingReasons.length > 0) {
      return {
        text: `Set. ${withdrew}Still in review: ${r.remainingReasons.join(', ')}. ${r.compose?.explanation ?? ''}`,
        kind: 'info',
      };
    }
    return {
      text: `Set. ${withdrew}No draft: ${r.compose?.reason ?? 'see the audit trail'}. ${r.compose?.explanation ?? ''}`,
      kind: 'info',
    };
  }

  /**
   * The same sentence for the request panel, whose chain may not have run at all.
   *
   * A priority change and a language change are both "saved" and are not the
   * same event: one moves a ticket up a list, the other rewrites the reply. The
   * toast says which happened rather than leaving the person to guess from
   * whether Approvals changed.
   */
  function requestToast(result: SetRequestResult): { text: string; kind: 'success' | 'info' | 'error' } {
    if (!result.recompute) {
      const changed = result.changes.map(c => c.field).join(', ');
      return { text: `Saved: ${changed}. Nothing was re-run — the pipeline does not read these.`, kind: 'success' };
    }
    return recomputeToast({ recompute: result.recompute, invalidated: result.invalidated });
  }

  /** One call site for all three fact-panel submissions of the request form. */
  function submitRequest(edit: RequestFactsEdit) {
    setRequest.mutate(edit, {
      onSuccess: (result) => {
        setEditingFact(null);
        const { text, kind } = requestToast(result);
        toast(text, kind, { sticky: true });
      },
      onError: (err: unknown) => toast(
        err instanceof Error ? err.message : 'Could not save the request details', 'error', { sticky: true },
      ),
    });
  }

  /**
   * One sentence for what the fan-out actually did (P26).
   *
   * Three outcomes have to be told apart, because they mean three different
   * next actions: a ticket that is drafting, a ticket the ladder parked at a
   * Sales Manager, and a ticket P21's hold caught as a probable duplicate of an
   * open one. Silence about the third would be the worst of the three — the
   * person would go looking in Approvals for a draft that will never come.
   */
  function splitToast(result: SplitProductsResult): { text: string; kind: 'success' | 'info' | 'error' } {
    const held = result.created.filter(c => c.heldAsDuplicateOf !== null);
    const awaiting = result.created.filter(c => c.status === 'AwaitingSalesApproval');
    const parts = [`${result.created.length} ticket(s) created: ${result.created.map(c => c.ticketNumber).join(', ')}.`];
    if (held.length > 0) {
      parts.push(
        `${held.length} held as a probable duplicate (${held.map(c => `${c.ticketNumber} of ${c.heldAsDuplicateOf?.ticketNumber}`).join(', ')}) `
        + '— those draft nothing until somebody decides.',
      );
    }
    if (awaiting.length > 0) {
      parts.push(`${awaiting.length} went to a Sales Manager to authorise — see Authorizations.`);
    }
    parts.push(result.parentClosed
      ? 'This request is closed; the new tickets carry the work.'
      : `${result.remainingFamilies} family/families are still unsplit, so this ticket stays open.`);
    parts.push('Nothing was sent. The agent writes the reply drafts on its next poll.');
    return {
      text: parts.join(' '),
      kind: held.length > 0 || awaiting.length > 0 ? 'info' : 'success',
    };
  }

  /**
   * One call site for both buttons. `allowLanguageMismatch` is the ONLY thing
   * that differs between "compose" and "override and compose" — the decider's
   * name is added server-side from the session, never from here.
   */
  function runCompose(opts: { allowLanguageMismatch?: boolean; allowEnglishReply?: boolean } = {}) {
    compose.mutate(opts, {
      onSuccess: (result) => {
        const { text, kind } = composeToast(result);
        // Sticky: this sentence is the whole answer to "what happened when I
        // pressed compose", and it is routinely a refusal with a reason.
        toast(text, kind, { sticky: true });
      },
      // A 409 is the pile-up guard doing its job ("this ticket already has a
      // draft") — the endpoint's message says which one, so show it verbatim
      // rather than a generic failure.
      onError: (err: unknown) => toast(
        err instanceof Error ? err.message : 'Compose failed',
        'error',
      ),
    });
  }

  /**
   * The ONE way into the fact editor — the three "Change" links and the review
   * guide's button all come through here.
   *
   * One fact is changed at a time, and that is a rule about the CHAIN, not
   * about screen real estate: every one of these edits withdraws the waiting
   * draft, may cancel an open Sales request and re-runs the purchase check
   * (`shared/ticket-facts.ts`). Two editors open at once invites a second
   * submit against a ticket the first one has just moved, and the person would
   * be reading consequences computed for the state before their own change.
   *
   * A click on another field is therefore not silently swallowed and does not
   * discard what is typed: it says why, and takes the reader to the panel that
   * is already open.
   */
  function openFactEditor(fact: 'customer' | 'product' | 'request') {
    if (editingFact && editingFact !== fact) {
      toast('One fact is changed at a time — finish or cancel the open change first.', 'info');
      revealPanel(factEditorRef.current);
      return;
    }
    setEditingFact(fact);
  }

  /*
   * P39, §12 and P38's rule applied to a screen P38 did not own.
   *
   * `Loading…` was the whole document and "Ticket not found." was a red line
   * with no code, no time and no way back — and it was shown for a 500 as
   * readily as for a 404, so a database blip and a bad URL read identically.
   * `LoadError` carries the code, the request id and the failing query's own
   * `refetch`; the skeleton keeps the shape so nothing jumps when the data lands.
   */
  if (isLoading) {
    return (
      <div>
        <LoadingAnnouncement what="the ticket" />
        <SkeletonCards count={3} />
      </div>
    );
  }
  if (isError) {
    return <LoadError what="This ticket" error={error} at={errorUpdatedAt} onRetry={() => { void refetch(); }} className="" />;
  }
  if (!ticket) {
    return (
      <Card title="No such ticket">
        <p className="text-sm text-body">
          There is no ticket numbered <span className="font-mono">{ticketNumber}</span>. It may have been typed by
          hand, or it may belong to another system.{' '}
          <Link to="/tickets" className="text-info hover:underline">Back to tickets</Link>.
        </p>
      </Card>
    );
  }

  const reasons = reviewReasons(ticket.reviewReason);
  const blockedByLanguage = reasons.includes(LANGUAGE_REVIEW_REASON);
  // The covering mail's language, which is a different question from the
  // document's — see REPLY_LANGUAGE_REVIEW_REASONS.
  const blockedByReplyLanguage = reasons.some(r => REPLY_LANGUAGE_REVIEW_REASONS.includes(r));
  const composable = !UNCOMPOSABLE.has(ticket.status);
  // "Compose again" rather than "Compose draft" whenever the ticket is sitting
  // in a state a previous attempt put it in — the reopen lane for "the template
  // was activated" / "the document arrived".
  const isRevive = ticket.status === 'NeedsReview' || reasons.length > 0;

  // A fact is "unsettled" when it is missing, or when a review reason still
  // disputes it. Both halves matter: `DRA-2026-00043` has NO customer, while a
  // ticket can also carry a resolved id AND `CustomerAmbiguous` from a run that
  // guessed. The panel is the way to answer either.
  const customerFactState = customerState(ticket);
  const productFactState = productState(ticket);
  const customerUnsettled = isUnsettled(customerFactState);
  const productUnsettled = isUnsettled(productFactState);
  const heldAsDuplicate = reasons.includes(DUPLICATE_REVIEW_REASON);
  // The panel shows whenever the server reported a relation in EITHER direction
  // — held or merely found, this ticket's duplicate or a ticket held against it.
  const hasDuplicateRelation = !!ticket.duplicateOf || (ticket.duplicates ?? []).length > 0;
  // P29/P26 — the same request, answered on several tickets. Never a hold: the
  // panel exists so a person can walk between them, not to ask them anything.
  const hasSplitRelation = !!ticket.splitFrom || (ticket.splitInto ?? []).length > 0;
  // The fan-out question. It replaces the article search rather than sitting
  // beside it: the request names no article, so there is nothing to search for.
  const askedForAllProducts = reasons.includes(PRODUCT_SCOPE_REVIEW_REASON);
  const delivery = ticket.delivery ?? [];

  // P28. A panel is on screen for one of two reasons, and they are different
  // things: the fact is MISSING (the P21 lane, unchanged), or a person has asked
  // to change one that is settled. Editing is refused entirely once a reply has
  // gone out — the endpoints answer 409, and offering a control that is
  // guaranteed to fail is the dead end this plan exists to remove.
  const factsEditable = canEdit && composable && delivery.length === 0;
  const showCustomerPanel = (canEdit && composable && customerUnsettled)
    || (factsEditable && editingFact === 'customer');
  const showProductPanel = (canEdit && composable && productUnsettled && !askedForAllProducts)
    || (factsEditable && editingFact === 'product');
  const showRequestPanel = factsEditable && editingFact === 'request';

  /**
   * The three fact panels, built once and placed in one of two slots.
   *
   * An EDIT — somebody pressed "Change" — renders in the slot directly under
   * the facts card, beside the link they pressed. An open QUESTION renders in
   * the lane further down, which is where the review guide's pointers send a
   * reader ("use the amber panel below") and where nobody has clicked anything.
   * Same component, same props, same `mode`; only the position differs, so
   * there is no second copy of a panel that could drift from the first.
   *
   * `mode` follows whether the fact IS settled and not where the panel sits: a
   * missing customer opened from the card is still "Which customer is this?",
   * because nothing is being changed — it is being answered for the first time.
   */
  const customerPanel = showCustomerPanel ? (
    <SetCustomerPanel
      ticketNumber={ticket.ticketNumber}
      rawText={ticket.rawCustomerText}
      mode={customerUnsettled ? 'answer' : 'edit'}
      currentName={ticket.customerName ?? ticket.customerId}
      pending={setCustomer.isPending}
      onChoose={setPendingCustomer}
      // Cancel closes what a person OPENED. An open question left in the lane
      // has no "leave it as it is" — that is the state the ticket is stuck in.
      onCancel={editingFact === 'customer' ? () => setEditingFact(null) : undefined}
    />
  ) : null;

  const productPanel = showProductPanel ? (
    <SetProductPanel
      ticketNumber={ticket.ticketNumber}
      rawText={ticket.rawProductText}
      companyCode={ticket.companyCode}
      mode={productUnsettled ? 'answer' : 'edit'}
      currentName={ticket.productName ?? ticket.productId}
      pending={setProduct.isPending || setProducts.isPending}
      onChoose={setPendingProduct}
      // P29. One pick and several picks come out of the same list; the
      // panel says which it is and this screen asks the matching question.
      onChooseMany={setPendingProducts}
      onCancel={editingFact === 'product' ? () => setEditingFact(null) : undefined}
    />
  ) : null;

  const requestPanel = showRequestPanel ? (
    <RequestFactsPanel
      ticket={ticket}
      reasons={reasons}
      consequences={ticket.factConsequences}
      pending={setRequest.isPending}
      onSubmit={(edit, rerunsChain) => {
        // A language change rewrites the reply and can withdraw a draft,
        // so it is signed for in a modal like the other two facts. A
        // priority or due-date change is not — a confirmation for
        // "moved to High" is the kind of dialog people learn to click
        // through, and then click through the ones that matter.
        if (rerunsChain && consequenceSentence(ticket.factConsequences)) setPendingRequest(edit);
        else submitRequest(edit);
      }}
      onCancel={() => setEditingFact(null)}
    />
  ) : null;

  // What the "Change" links opened, if anything. Null while a link is pressed
  // on a ticket whose panel cannot show — the slot then stays closed rather
  // than scrolling the reader to an empty box.
  const activeFactPanel = editingFact === 'customer' ? customerPanel
    : editingFact === 'product' ? productPanel
      : editingFact === 'request' ? requestPanel
        : null;

  /**
   * The Compose control, built here and placed by the Reply card (P39).
   *
   * It used to be a card of its own directly under that one, which produced two
   * primary buttons below the facts — both of which create a draft in Approvals
   * — and drew this one even where `compose-draft.ts` answers 409 because a
   * draft is already waiting. The button, the mutation, the gates and the
   * wording are unchanged; what moved is where it appears and, for two send
   * states, whether it appears at all.
   */
  const composeAction = (
    <div>
      <Button variant="primary" size="sm" disabled={compose.isPending} onClick={() => runCompose()}>
        {compose.isPending ? 'Composing…' : isRevive ? 'Compose again' : 'Compose draft'}
      </Button>
      <p className="mt-2 text-xs text-muted">
        {isRevive
          ? 'Runs the search and the reply template again — the move once a template was activated or the document arrived. Creates a draft; it still has to be approved in '
          : 'Runs the document search and writes a reply from the approved template. Creates a draft; nothing is sent until it is approved in '}
        <Link to="/approvals" className="text-info hover:underline">Approvals</Link>.
      </p>
    </div>
  );

  // The status form's derived state, all three pieces of it.
  const targetStatus = newStatus || ticket.status;
  // A sent reply plus a terminal status is the reopen the endpoint refuses.
  const reopenBlocked = delivery.length > 0 && TERMINAL_STATUSES.has(ticket.status);
  const statusChangeReady = !!newStatus && newStatus !== ticket.status
    && (newStatus !== 'Closed' || !!closeReason);
  const leavingReview = ticket.status === 'NeedsReview' && targetStatus !== 'NeedsReview'
    && targetStatus !== ticket.status && reasons.length > 0;

  const entries = (caseFile ?? []).filter(e => CASE_FILE_KINDS.has(e.entryKind));
  // audit-search?ticketNumber=…&includeRun=1 replaces the vTicketCaseFile slice
  // here — that view filters TicketId IS NOT NULL and drops Actor/DetailJson/
  // CorrelationId (P9's "2 of 7 rows" finding); this carries all three.
  const auditEntries = auditPage?.data ?? [];

  return (
    <div>
      {/* P31. A ticket opened FROM the review queue keeps the queue: back to the
          board exactly as it was, and one click to the next question. Anything
          else — a reload, a bookmark, a link somebody pasted — has no such
          context and gets the plain link it always had. */}
      {isReviewOrigin(origin)
        ? <ReviewQueueNav ticketNumber={ticket.ticketNumber} origin={origin} />
        : <Link to={typeof origin === 'string' ? origin : '/tickets'} className="text-sm text-info hover:underline">&larr; Back to tickets</Link>}

      {/* =====================================================================
          THE PAGE HEAD (P39, audit S2-14)
          =====================================================================
          The status used to be `text-sm text-gray-500` UNDER the number — the one
          fact the page exists to report, carrying the least weight on it, while
          every board row gave the same word a coloured chip.

          It is the board's own `StatusPill` now, level with the number, taking
          its word, its tint and its glyph from `lib/labels.ts`'s three helpers —
          the same three the row uses, so a ticket and its row cannot say
          different things. No `board` argument is passed: that one is the
          `/review` neutralisation, which exists because every row on that board
          needs a person. A ticket screen is one ticket and has no such
          background to stand out from. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-2xl font-bold leading-tight text-ink">{ticket.ticketNumber}</h1>
        <StatusPill
          tone={ticketStatusTone(ticket)}
          glyphName={ticketStatusGlyph(ticket)}
          title={`${ticketStatusTooltip(ticket)}\n\nCode: ${ticket.status}`}
        >
          {ticketStatusLabel(ticket)}
        </StatusPill>
        {/* Roman's decision K3. A badge and nothing else — the fixtures stay
            visible and stay counted, and a person testing on one is told. */}
        {ticket.isTestData && <Badge title={TEST_DATA_LABEL.tooltip}>{TEST_DATA_LABEL.label}</Badge>}
        {ticket.closeReason && (
          <Badge title={tooltipFor(CLOSE_REASON_LABELS, ticket.closeReason)}>
            {labelFor(CLOSE_REASON_LABELS, ticket.closeReason)}
          </Badge>
        )}
        {/* Beside the word, not repeating it — see reviewReasonsBesideStatus.
            This is the TICKET HEADER rule and it is deliberately not the board's:
            there the two sit in separate columns, one of which a reviewer filters
            by family, so blanking it would make the board contradict the filter
            that fetched the row (P38 §4.1). Here they are one line. */}
        {reviewReasonsBesideStatus(ticket).length > 0
          && <ReviewReasonChips reasons={reviewReasonsBesideStatus(ticket)} />}
      </div>

      {/* =====================================================================
          EVERY DETECTED FACT IS EDITABLE (P28)
          =====================================================================
          Until this phase the panels below appeared only while a fact was
          MISSING and vanished once it was answered — so a wrong answer was
          permanent. `DRA-2026-00044` was the live proof: the customer had been
          settled in the wrong company, and the only control on the screen
          searched that wrong company's catalogue.

          A ticket whose reply has already been SENT shows the P24 pointer
          instead of a Change link. Editing a fact there would compose a second,
          contradictory mail into a thread the customer has already read; the
          endpoints answer 409 and the correction panel below is the way in. */}
      {/* =====================================================================
          TWO COLUMNS, A 392 px RAIL (P39, Roman's decision D6 — reference §6)
          =====================================================================
          Left is the work: the facts and their editors, the reply, the status and
          the case file. Right is what the ticket reports and what happened to it.

          The rail is `xl:` and not unconditional, because §4's minimum width is
          1280 px and the DRA's content sits beside a 240 px sidebar: at 1280 the
          work column would be 600 px, which is narrower than the customer search
          panel needs. Below `xl` the two columns stack in reading order — work
          first — and nothing is hidden. */}
      <div className="mt-4 flex flex-col items-start gap-6 xl:flex-row">
        <div className="w-full min-w-0 space-y-6 xl:flex-1">

      <Card title="Detected facts" subtitle="What the agent read out of the mail. Each of these can be corrected, and correcting one re-runs the checks that depend on it.">
      <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
        <div>
          <FactLabel
            label="Customer"
            canChange={canEdit && composable && delivery.length === 0}
            sealed={delivery.length > 0}
            active={editingFact === 'customer'}
            blocked={!!editingFact && editingFact !== 'customer'}
            onChange={() => openFactEditor('customer')}
            onCancel={() => setEditingFact(null)}
          />
          <div className="flex items-center gap-1.5">
            <NameWithCode name={ticket.customerName} code={ticket.customerCode} raw={ticket.rawCustomerText ?? ticket.customerId} state={customerFactState} />
            <BcLink url={ticket.bcCustomerUrl} />
            <HubspotChip url={ticket.hubspotUrl} />
          </div>
        </div>
        <div>
          <FactLabel
            label="Product"
            canChange={canEdit && composable && delivery.length === 0}
            sealed={delivery.length > 0}
            active={editingFact === 'product'}
            blocked={!!editingFact && editingFact !== 'product'}
            onChange={() => openFactEditor('product')}
            onCancel={() => setEditingFact(null)}
          />
          <div className="flex items-center gap-1.5">
            <NameWithCode name={ticket.productName} code={ticket.productCode} raw={ticket.rawProductText ?? ticket.productId} state={productFactState} />
            <BcLink url={ticket.bcProductUrl} />
          </div>
        </div>
        <div>
          <FactLabel
            label="Document type & language"
            canChange={canEdit && composable && delivery.length === 0}
            sealed={delivery.length > 0}
            active={editingFact === 'request'}
            blocked={!!editingFact && editingFact !== 'request'}
            onChange={() => openFactEditor('request')}
            onCancel={() => setEditingFact(null)}
          />
          <div title={ticket.requestedLanguage ? 'The language the customer asked the document in.' : 'The mail did not state a language.'}>
            {ticket.requestedDocTypeCode} ({ticket.requestedLanguage ?? '—'})
          </div>
        </div>
        <div>
          <FactLabel
            label="Priority & due"
            // Same gate as the other three, and not the weaker `canEdit &&
            // composable` it used to carry: priority and due date are edited in
            // the SAME panel as the language, and `set-request.ts` answers 409
            // once a reply has gone out. The link would have blocked the other
            // two fields (one at a time) while opening nothing at all.
            canChange={canEdit && composable && delivery.length === 0}
            sealed={delivery.length > 0}
            active={editingFact === 'request'}
            blocked={!!editingFact && editingFact !== 'request'}
            onChange={() => openFactEditor('request')}
            onCancel={() => setEditingFact(null)}
          />
          {/* Its own label says "& due", so the due date belongs in it. It used
              to sit in a cell of its own two columns away, which made a card of
              eight cells out of six facts and left `Due —` alone on a row. */}
          <div>
            {ticket.priority}
            <span className="text-muted"> · due </span>
            <span className="tabular-nums">{isoDate(ticket.dueAt)}</span>
          </div>
        </div>
      </div>
      </Card>

      {/* =====================================================================
          THE EDITOR SITS WHERE THE LINK WAS PRESSED
          =====================================================================
          Roman, 2026-09-03. Until now every panel rendered in the lane below
          the delivery history, the language panel and the review guide — on
          `DRA-2026-00048` that put the editor about 900 pixels under the
          "Change" link, with its Save button off the bottom of the screen, and
          a person who did not scroll saw nothing happen at all.

          Only ONE panel can be here, and that is the point rather than a
          consequence of the layout: each of these edits withdraws the waiting
          draft, may cancel an open Sales request and re-runs the purchase
          check, so a second editor would be showing consequences computed for
          a ticket the first one is about to move. */}
      {activeFactPanel && <div ref={factEditorRef}>{activeFactPanel}</div>}

      {/* P37a. The reply, between the facts it was written from and the proof
          that it went out — which is the order the work happens in. It renders
          itself away once the mail has been sent, because `DeliveryProofPanel`
          below says that in more detail and two cards about one sent mail would
          be two sources for one fact. */}
      {/* P37b. `canReply` is the same gate the Compose button carries, and for
          the same reason: a terminal ticket is reopened first (the status
          dropdown, or a correction where one has been sent), and offering a
          control that the endpoint would refuse is the dead end this plan
          exists to remove. `delivery.length` is not in it — once a mail has
          gone out the card renders nothing at all. */}
      {/* P39. The compose control lives INSIDE this card now — see ReplyCard's
          header for the two defects that moved it: two primary buttons below the
          facts, both producing the same thing, and one of them drawn on a ticket
          whose endpoint answers 409 every time. The node is built here because
          the mutation, the gates and the toasts are here; where it is drawn, and
          whether it is drawn at all, is the card's. */}
      <ReplyCard
        ticket={ticket}
        canReply={canEdit && composable}
        composeAction={canEdit && composable && delivery.length === 0 ? composeAction : undefined}
      />

      {delivery.map(d => <DeliveryProofPanel key={d.draftId} proof={d} />)}

      {/* The ticket's own delivered-document columns, kept as a fallback for a
          ticket closed by hand (no draft, so no proof row above). */}
      {delivery.length === 0 && ticket.status === 'Closed' && ticket.deliveredDocUrl && (
        <div className="rounded-card border border-brand-100 bg-brand-50 p-4 text-sm">
          <h2 className="text-sm font-semibold text-brand-700">Delivered</h2>
          <a
            href={ticket.deliveredDocUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-block text-brand-700 hover:underline"
          >
            {ticket.deliveredFileName ?? ticket.deliveredDocUrl}
          </a>
        </div>
      )}

      <CustomerHistoryPanel ticket={ticket} />

      {/* Same customer, same article, another language — a relative, never a
          duplicate (Roman's decision 3 keeps the language in the key). */}
      <RelatedLanguagePanel ticket={ticket} />

      {/* Every open question on this ticket, each with the control that answers
          it (P22). The controls themselves stay where they are — this is the
          index, not a second set of buttons. */}
      <ReviewReasonGuide
        // The DISPLAY list, stored plus derived (P37a). `reasons` itself stays
        // the stored column everywhere else on this screen: `isRevive`,
        // `leavingReview` and the three blocked-by checks all branch on it, and
        // a derived entry in that list would quietly change what those mean.
        reasons={allReviewReasons(ticket)}
        canEdit={canEdit && composable}
        requestedLanguage={ticket.requestedLanguage}
        docTypeCode={ticket.requestedDocTypeCode}
        recomputePending={recompute.isPending}
        // P28 — the five question-only reasons finally have a control, and the
        // index that lists them is where a reviewer is standing when they read
        // the question. A ticket whose reply has gone out gets no button: the
        // endpoint answers 409 and the correction panel is the way in.
        onEditRequest={delivery.length === 0 ? () => openFactEditor('request') : undefined}
        onRecompute={() => recompute.mutate(undefined, {
          onSuccess: (result) => {
            const { text, kind } = recomputeToast(result);
            toast(text, kind, { sticky: true });
          },
          onError: (err: unknown) => toast(
            err instanceof Error ? err.message : 'Could not run the check again', 'error',
          ),
        })}
      />

      {/* =====================================================================
          THE RECOMPUTE LANE (P21)
          =====================================================================
          Shown while the fact is missing or disputed, and gone once it is
          settled — a ticket whose customer resolved cleanly does not need a
          panel asking who the customer is. A closed case is not corrected here
          (the endpoint answers 409); it is reopened first, deliberately. */}
      {((customerPanel && editingFact !== 'customer') || (productPanel && editingFact !== 'product')
        || askedForAllProducts && canEdit && composable
        || hasDuplicateRelation || hasSplitRelation) && (
        <div className="space-y-4">
          {hasSplitRelation && <SplitRelationsPanel ticket={ticket} />}
          {hasDuplicateRelation && (
            <DuplicatePanel
              ticket={ticket}
              held={heldAsDuplicate}
              canEdit={canEdit && composable}
              merging={mergeDuplicate.isPending}
              releasing={notDuplicate.isPending}
              onMerge={() => setPendingDuplicateAction('merge')}
              onNotDuplicate={() => setPendingDuplicateAction('notDuplicate')}
            />
          )}
          {editingFact !== 'customer' && customerPanel}
          {askedForAllProducts && canEdit && composable && (
            <SplitProductsPanel
              ticketNumber={ticket.ticketNumber}
              pending={splitProducts.isPending}
              onSplit={(productIds, families) => setPendingSplit({ productIds, families })}
            />
          )}
          {editingFact !== 'product' && productPanel}
          {/* The request panel has no lane copy: it only ever exists because
              somebody pressed "Change", so it is always in the slot above. */}
        </div>
      )}

      {pendingCustomer && (
        <ConfirmModal
          title={pendingCustomer.isEdit ? 'Change the customer on this ticket?' : 'Set the customer on this ticket?'}
          message={
            `${pendingCustomer.candidate.customerName} (${pendingCustomer.candidate.customerId}, company `
            + `${pendingCustomer.candidate.companyCode}) becomes the customer of ${ticket.ticketNumber}. `
            + 'The system then re-checks for a duplicate, re-runs the purchase check — which may ask a Sales '
            + 'Manager to authorise the request — and composes a reply draft if everything is settled. Nothing '
            + 'is sent: a draft still has to be approved in Approvals. Your name is recorded against this choice.'
            + (ticket.companyCode && ticket.companyCode !== pendingCustomer.candidate.companyCode
              ? ` The ticket moves from company ${ticket.companyCode} to ${pendingCustomer.candidate.companyCode}, `
                + 'so its product is looked up again there and dropped if it does not resolve.'
              : '')
            // P28. What this click costs, from the server's own count and not
            // from anything this screen worked out. Empty when there is nothing
            // to withdraw, because a warning that is always there is not read.
            + (pendingCustomer.isEdit && consequenceSentence(pendingCustomer.consequences)
              ? ` The customer was already settled, so: ${consequenceSentence(pendingCustomer.consequences)}`
              : '')
          }
          confirmLabel={pendingCustomer.isEdit ? 'Yes - change the customer' : 'Yes - set the customer'}
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const chosen = pendingCustomer.candidate;
            setPendingCustomer(null);
            setEditingFact(null);
            setCustomer.mutate(
              { customerId: chosen.customerId, companyCode: chosen.companyCode },
              {
                onSuccess: (result) => {
                  const { text, kind } = recomputeToast(result);
                  toast(text, kind, { sticky: true });
                },
                onError: (err: unknown) => toast(
                  err instanceof Error ? err.message : 'Could not set the customer', 'error', { sticky: true },
                ),
              },
            );
          }}
          onCancel={() => setPendingCustomer(null)}
        />
      )}

      {pendingProduct && (
        <ConfirmModal
          title={pendingProduct.isEdit ? 'Change the article on this ticket?' : 'Set the product on this ticket?'}
          message={
            `${pendingProduct.candidate.productName} (${pendingProduct.candidate.productId}) becomes the article of `
            + `${ticket.ticketNumber}. The purchase check is asked again for THIS article - which decides whether `
            + 'the document may go out at all - and a reply draft is composed if everything is settled. Nothing is '
            + 'sent: a draft still has to be approved in Approvals. Your name is recorded against this choice.'
            + (pendingProduct.isEdit && consequenceSentence(pendingProduct.consequences)
              ? ` The article was already settled, so: ${consequenceSentence(pendingProduct.consequences)}`
              : '')
          }
          confirmLabel={pendingProduct.isEdit ? 'Yes - change the article' : 'Yes - set the product'}
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const chosen = pendingProduct.candidate;
            setPendingProduct(null);
            setEditingFact(null);
            setProduct.mutate({ productId: chosen.productId }, {
              onSuccess: (result) => {
                const { text, kind } = recomputeToast(result);
                toast(text, kind, { sticky: true });
              },
              onError: (err: unknown) => toast(
                err instanceof Error ? err.message : 'Could not set the product', 'error', { sticky: true },
              ),
            });
          }}
          onCancel={() => setPendingProduct(null)}
        />
      )}

      {/* P29. Several articles at once. The count is in the title AND in the
          body, because the number of tickets is the number of separate mails
          this customer receives — and the FIRST id goes through the ordinary
          set-product path, so the withdrawal sentence belongs here too. */}
      {pendingProducts && pendingProducts.candidate.length > 0 && (
        <ConfirmModal
          title={pendingProducts.candidate.length === 1
            ? 'Set the article on this ticket?'
            : `Set 1 article and create ${pendingProducts.candidate.length - 1} ticket${pendingProducts.candidate.length - 1 === 1 ? '' : 's'}?`}
          message={
            `${pendingProducts.candidate[0].productName || pendingProducts.candidate[0].productId} becomes the `
            + `article of ${ticket.ticketNumber}.`
            + (pendingProducts.candidate.length > 1
              ? ` The other ${pendingProducts.candidate.length - 1} `
                + `(${pendingProducts.candidate.slice(1).map(p => p.productName || p.productId).join(', ')}) `
                + 'each become a ticket of their own, on this same mail thread. Every ticket is a separate '
                + 'purchase check and, in the pilot, a separate mail to the customer. A ticket whose article '
                + 'another open ticket already covers is held as a probable duplicate instead of being answered '
                + 'twice.'
              : ' That is exactly the ordinary "set the article" — nothing else is raised.')
            + ' Nothing is sent: every draft still has to be approved in Approvals. Your name is recorded '
            + 'against this choice.'
            // The first id is an EDIT when the ticket already carries an
            // article, and an edit withdraws what the old one produced. Same
            // sentence as the single pick, from the same server-side count.
            + (pendingProducts.isEdit && consequenceSentence(pendingProducts.consequences)
              ? ` The article was already settled, so: ${consequenceSentence(pendingProducts.consequences)}`
              : '')
          }
          confirmLabel={pendingProducts.candidate.length === 1
            ? 'Yes - set the article'
            : `Yes - set 1, create ${pendingProducts.candidate.length - 1}`}
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const chosen = pendingProducts.candidate;
            setPendingProducts(null);
            setEditingFact(null);
            setProducts.mutate({ productIds: chosen.map(p => p.productId) }, {
              onSuccess: (result) => {
                const { text, kind } = recomputeToast(result);
                const siblings = result.split;
                const extra = siblings && siblings.created.length > 0
                  ? ` ${siblings.created.length} further ticket(s): `
                    + `${siblings.created.map(c => c.ticketNumber).join(', ')}. They draft nothing until the `
                    + 'agent\u2019s next poll, and nothing is sent.'
                  : '';
                const refused = siblings && siblings.rejected.length > 0
                  ? ` Refused: ${siblings.rejected.map(r => `${r.key} (${r.reason})`).join('; ')}.`
                  : '';
                toast(`${text}${extra}${refused}`, kind, { sticky: true });
              },
              onError: (err: unknown) => toast(
                err instanceof Error ? err.message : 'Could not set the articles', 'error', { sticky: true },
              ),
            });
          }}
          onCancel={() => setPendingProducts(null)}
        />
      )}

      {/* The language is the third fact that rewrites the reply, so it is signed
          for like the other two. The modal appears only when there is something
          to withdraw — see the panel's onSubmit. */}
      {pendingRequest && (
        <ConfirmModal
          title="Change the requested language?"
          message={
            `The reply for ${ticket.ticketNumber} will be written for `
            + `${pendingRequest.requestedLanguage ?? 'no stated language, which lets the customer\u2019s own market decide'}`
            + ', and the document search is run again for it. '
            + `${consequenceSentence(ticket.factConsequences)} `
            + 'Your name is recorded against this choice.'
          }
          confirmLabel="Yes - change it"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const edit = pendingRequest;
            setPendingRequest(null);
            submitRequest(edit);
          }}
          onCancel={() => setPendingRequest(null)}
        />
      )}

      {/* The fan-out (P26). The count is in the title AND in the body, because
          the number of tickets is the number of separate mails this customer
          will receive — the pilot answers each ticket on its own, and bundling
          them into one reply is on the Phase-3 list. */}
      {pendingSplit && (
        <ConfirmModal
          title={`Create ${pendingSplit.productIds.length} ticket${pendingSplit.productIds.length === 1 ? '' : 's'}?`}
          message={
            `${ticket.ticketNumber} asked for the documents of every product this customer buys. `
            + `${pendingSplit.productIds.length} product famil`
            + `${pendingSplit.productIds.length === 1 ? 'y' : 'ies'} `
            + `(${pendingSplit.families.slice(0, 6).map(f => f.displayName ?? f.productId).join(', ')}`
            + `${pendingSplit.families.length > 6 ? `, and ${pendingSplit.families.length - 6} more` : ''}) `
            + `become ${pendingSplit.productIds.length === 1 ? 'a ticket' : 'that many tickets'}, each with its own `
            + 'purchase check and its own reply. In the pilot that means one mail per ticket. A family another open '
            + 'ticket already covers is held as a probable duplicate instead of being answered twice. Nothing is '
            + 'sent: every draft still has to be approved in Approvals. Your name is recorded against this choice.'
          }
          confirmLabel={`Yes - create ${pendingSplit.productIds.length}`}
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const chosen = pendingSplit;
            setPendingSplit(null);
            splitProducts.mutate({ productIds: chosen.productIds }, {
              onSuccess: (result) => {
                const { text, kind } = splitToast(result.split);
                toast(text, kind, { sticky: true });
              },
              onError: (err: unknown) => toast(
                err instanceof Error ? err.message : 'Could not split the request', 'error',
              ),
            });
          }}
          onCancel={() => setPendingSplit(null)}
        />
      )}

      {/* P27. The two duplicate decisions. Both name the OTHER ticket in the
          message, because that is the fact the person is signing for — and the
          target is the server's, not this screen's: the modal states what the
          server already resolved. */}
      {pendingDuplicateAction && ticket.duplicateOf && (
        <ConfirmModal
          title={pendingDuplicateAction === 'merge'
            ? `Merge ${ticket.ticketNumber} into ${ticket.duplicateOf.ticketNumber}?`
            : `Are ${ticket.ticketNumber} and ${ticket.duplicateOf.ticketNumber} different requests?`}
          message={pendingDuplicateAction === 'merge'
            ? `${ticket.ticketNumber} is closed as a duplicate and its mail moves onto `
              + `${ticket.duplicateOf.ticketNumber}, so the whole conversation stays in one case file. The customer `
              + `is answered from ${ticket.duplicateOf.ticketNumber} in the ordinary way — nothing is sent by this `
              + 'action, and nothing is deleted. Your name is recorded against this choice.'
            : `${ticket.ticketNumber} is released as a request of its own: the purchase check runs — which may ask a `
              + 'Sales Manager to authorise it — and a reply is drafted into ITS mail thread. The customer may '
              + `therefore receive two mails, one per ticket. The decision holds for this pair: `
              + `${ticket.duplicateOf.ticketNumber} will not stop this ticket again. Nothing is sent: a draft still `
              + 'has to be approved in Approvals. Your name is recorded against this choice.'}
          confirmLabel={pendingDuplicateAction === 'merge' ? 'Yes - merge' : 'Yes - they are different'}
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => {
            const action = pendingDuplicateAction;
            const other = ticket.duplicateOf?.ticketNumber;
            setPendingDuplicateAction(null);
            if (action === 'merge') {
              mergeDuplicate.mutate(undefined, {
                onSuccess: (result) => toast(
                  `${ticket.ticketNumber} closed as a duplicate of ${result.mergedInto.ticketNumber} — its mail is `
                  + 'now in that ticket’s case file. Answer it there.',
                  'success', { sticky: true },
                ),
                onError: (err: unknown) => toast(
                  err instanceof Error ? err.message : 'Could not merge the tickets', 'error', { sticky: true },
                ),
              });
            } else {
              notDuplicate.mutate(undefined, {
                onSuccess: (result) => {
                  const { text, kind } = recomputeToast(result);
                  toast(`Released from ${other}. ${text}`, kind, { sticky: true });
                },
                onError: (err: unknown) => toast(
                  err instanceof Error ? err.message : 'Could not release the ticket', 'error', { sticky: true },
                ),
              });
            }
          }}
          onCancel={() => setPendingDuplicateAction(null)}
        />
      )}


      {/* Once a reply has gone out, the language question is answered — and the
          delivery-proof panel above already states, in red, that it went out
          under an override. Offering the decision again here would be stale. */}
      {canEdit && blockedByLanguage && delivery.length === 0 && (
        <LanguageOverridePanel
          ticket={ticket}
          pending={compose.isPending}
          onOverride={() => setConfirmOverride(true)}
        />
      )}

      {/* The covering MAIL's language (P23). Shown beside the red C9 panel when
          both stand, never merged with it: two decisions, two buttons, two
          audit rows. Same "already delivered" suppression, for the same reason. */}
      {canEdit && blockedByReplyLanguage && delivery.length === 0 && (
        <ReplyLanguagePanel
          ticket={ticket}
          reasons={reasons}
          pending={compose.isPending}
          onOverride={() => setConfirmEnglishReply(true)}
        />
      )}

      {/* A ticket whose reply already went out is not composed for again — the
          endpoint answers 409 on a Sent draft, and offering a control that is
          guaranteed to fail is the dead end this plan exists to remove. What
          replaces it is not a sentence but an action (P24). */}
      {canEdit && delivery.length > 0 && <CorrectionPanel ticket={ticket} />}

      {confirmOverride && (
        <ConfirmModal
          title="Deliver a document in the wrong language?"
          message={
            `The customer asked for ${ticket.requestedLanguage ?? 'a language this request never stated'} and no `
            + `${ticket.requestedDocTypeCode} exists in it. Confirming attaches the best document that DOES exist, in `
            + 'another language. Your name is recorded against that decision in the audit trail, and the approver sees '
            + 'the mismatch in red before sending. Marcello (2026-08-26) names one case where this is defensible: a '
            + 'product not classified under CLP, for which no local-language sheet is legally required. Nothing in the '
            + 'ERP records CLP status, so only you can judge that.'
          }
          confirmLabel="Yes — deliver anyway"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => { setConfirmOverride(false); runCompose({ allowLanguageMismatch: true }); }}
          onCancel={() => setConfirmOverride(false)}
        />
      )}

      {confirmEnglishReply && (
        <ConfirmModal
          title="Write the covering mail in English?"
          message={
            'The rule says this reply should be written in another language, and there is no approved wording in it. '
            + 'Confirming writes the mail from the ENGLISH template instead. Your name is recorded against that '
            + 'decision. This is about the covering note only - it does not change which document is attached, and it '
            + 'is not the same as sending a data sheet in the wrong language. Roman agreed this route on 2026-09-01 '
            + 'as the way through until the wording exists in every language; the lasting fix is to write it under '
            + 'Masters.'
          }
          confirmLabel="Yes - answer in English"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => { setConfirmEnglishReply(false); runCompose({ allowEnglishReply: true }); }}
          onCancel={() => setConfirmEnglishReply(false)}
        />
      )}

      {canEdit && (
        <div className="space-y-6">
          <Card title="Status">
            {/* THE ONE MOVE THIS SCREEN REFUSES (decision 6). A reply has gone
                out; dragging the ticket back into open work is what happened to
                DRA-2026-00045, and it produced a ticket that could never be
                composed for again because a Sent draft blocks recomposition.
                The move is not offered at all rather than offered and then
                refused by the endpoint. */}
            {reopenBlocked ? (
              <p className="text-sm text-body">
                A reply for this ticket has already been sent, so it cannot be moved back into open work from here.
                Correcting a delivered answer means a new mail in the same thread, approved the normal way — use
                <strong> Send a correction</strong> above.
              </p>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                {/* P39, §4: both selects measured 21 px high. They are the kit's
                    now — 36 px, an `--edge` border and the one focus ring — and
                    the width sits on the WRAPPER, because `Select` is `w-full` and
                    a competing width class on the element itself is decided by
                    Tailwind's key order rather than by whoever wrote it last. */}
                <div className="w-56">
                  <Field label="Move to">
                    {fp => (
                      <Select
                        {...fp}
                        value={targetStatus}
                        onChange={e => { setNewStatus(e.target.value); if (e.target.value !== 'Closed') setCloseReason(''); }}
                      >
                        {TICKET_STATUSES.map(st => (
                          <option key={st} value={st} title={tooltipFor(TICKET_STATUS_LABELS, st)}>
                            {labelFor(TICKET_STATUS_LABELS, st)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>
                {targetStatus === 'Closed' && (
                  <div className="w-56">
                    <Field
                      label={<>Close reason <span className="text-err">*</span></>}
                      error={closeReason ? null : 'A closed ticket has to say how it ended.'}
                    >
                      {fp => (
                        <Select {...fp} value={closeReason} onChange={e => setCloseReason(e.target.value)}>
                          <option value="">Choose one…</option>
                          {CLOSE_REASONS.map(r => (
                            <option key={r} value={r} title={tooltipFor(CLOSE_REASON_LABELS, r)}>
                              {labelFor(CLOSE_REASON_LABELS, r)}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </div>
                )}
                {/* Secondary, not primary (P39, task 5): §7 allows one primary per
                    view and this screen's is the reply. Moving a ticket by hand is
                    the exception — the ordinary way out of a status is to answer
                    the question that put it there. */}
                <Button variant="secondary" size="sm"
                  disabled={!statusChangeReady || updateStatus.isPending}
                  // Closing without a reason was a 400 with a generic toast —
                  // Marcello hit exactly that. The requirement is stated here,
                  // in the form, before the click, and now in the field as well.
                  disabledReason={targetStatus === 'Closed' && !closeReason
                    ? 'Pick a close reason first — a closed ticket has to say how it ended.'
                    : 'Choose a status this ticket is not already in.'}
                  onClick={() => updateStatus.mutate(
                    { status: newStatus, closeReason: newStatus === 'Closed' ? closeReason : undefined },
                    {
                      onSuccess: () => { setNewStatus(''); setCloseReason(''); toast('Status updated', 'success'); },
                      onError: (err: unknown) => toast(
                        err instanceof Error ? err.message : 'Failed to update status', 'error', { sticky: true },
                      ),
                    }
                  )}
                >
                  Update status
                </Button>
                {STATUS_SIDE_EFFECTS[targetStatus] && targetStatus !== ticket.status && (
                  <p className="basis-full text-xs text-warn-ink">{STATUS_SIDE_EFFECTS[targetStatus]}</p>
                )}
                {leavingReview && (
                  <p className="basis-full text-xs text-warn-ink">
                    Moving out of review CLEARS the review reason — it is not written back. If the question is still
                    open, answer it above instead.
                  </p>
                )}
              </div>
            )}
          </Card>

          <AssignPanel
            assignedTo={ticket.assignedTo}
            assignees={assignees ?? []}
            currentUserEmail={user?.email}
            pending={assign.isPending}
            onAssign={(email) => assign.mutate(email, {
              onSuccess: () => toast(
                email ? `Assigned to ${email}` : 'Assignment removed — the ticket is back in the unassigned queue',
                'success',
              ),
              onError: () => toast('Failed to assign', 'error'),
            })}
          />
        </div>
      )}

      {/* The case file, out of the tab it shared with the ledger. Adding to it
          is an action, so it belongs in the work column; the ledger went to the
          rail (`TicketHistoryCard`), where a person can read it while working.
          Neither is behind a tab now, so neither is hidden behind a click. */}
      <CaseFileCard
        entries={entries}
        canEdit={canEdit}
        noteTitle={noteTitle}
        noteDetail={noteDetail}
        pending={addNote.isPending}
        onTitleChange={setNoteTitle}
        onDetailChange={setNoteDetail}
        onAdd={() => addNote.mutate({ title: noteTitle, detail: noteDetail || undefined }, {
          onSuccess: () => { setNoteTitle(''); setNoteDetail(''); toast('Note added', 'success'); },
          onError: () => toast('Failed to add note', 'error'),
        })}
      />

        </div>

        {/* THE RAIL. 392 px is Roman's decision D6 and the reference's figure;
            it is a fixed width rather than a fraction so the work column takes
            every pixel a wider screen adds, which is where the customer search,
            the candidate lists and the delivery table live. */}
        <aside className="w-full shrink-0 space-y-6 xl:w-[392px]">
          <TicketDetailsCard ticket={ticket} />
          <TicketHistoryCard
            ticketNumber={ticket.ticketNumber}
            entries={auditEntries}
            expandedId={expandedAuditId}
            onToggle={(id) => setExpandedAuditId(expandedAuditId === id ? null : id)}
          />
        </aside>
      </div>

    </div>
  );
}

/**
 * A fact's label on the header card, with the way to change it (P28).
 *
 * Three states, and the third is the one that matters: a ticket whose reply has
 * already been sent shows neither a link nor nothing at all, but a word saying
 * why — because "no button" is indistinguishable from "no permission", and the
 * way back in (the correction panel further down) is not obvious from a missing
 * link.
 */
export function FactLabel({ label, canChange, sealed, active, blocked, onChange, onCancel }: {
  label: string;
  canChange: boolean;
  sealed: boolean;
  /** This field is the one currently open in the editor below the card. */
  active?: boolean;
  /** Another field is open. The rule is one at a time, so this link is inert. */
  blocked?: boolean;
  onChange: () => void;
  onCancel?: () => void;
}) {
  // The link follows its own label immediately, and is NOT pushed to the far
  // edge of the grid column. Roman, 2026-09-03: with `justify-between` the
  // "Change" belonging to Customer came to rest a few pixels left of the word
  // "Product", so on a wide window it read as that column's control — three
  // links, each apparently labelling the wrong fact. Proximity is what says
  // which fact a control changes; alignment does not.
  return (
    <span className="flex items-baseline gap-2">
      <span className={active ? 'font-medium text-amber-800' : 'text-gray-500'}>{label}</span>
      {/* The link states which of the three it is, so the rule is visible on the
          card and not only in the toast that explains a click that did nothing.
          Two fields map to the same editor — the document type and the priority
          are both edited in "What was asked for?" — and both say so. */}
      {/* P36, S2-10: these two measured 16 px high — the four `Change`
          controls the audit named. They are the kit's quiet button now, so the
          hit target, the focus ring and the disabled appearance all arrive
          from one place. A blocked one no longer says so in `gray-300`
          (2.0:1 against white): a disabled kit button is `--head` ground with
          `--faint` text and states its reason in the `title`. */}
      {canChange && active && onCancel && (
        <Button variant="quiet" size="sm" className="underline hover:no-underline" onClick={onCancel}>
          Cancel
        </Button>
      )}
      {canChange && !active && (
        <Button
          variant="quiet"
          size="sm"
          disabled={blocked}
          disabledReason="One fact is changed at a time. Finish or cancel the open change first."
          onClick={onChange}
        >
          Change
        </Button>
      )}
      {sealed && (
        <span
          className="text-xs text-muted"
          title={'A reply for this ticket has already been sent, so its facts are not edited here — a second, '
            + 'contradictory mail would go into a thread the customer has already read. '
            + 'Use "Send a correction" below.'}
        >
          sent — correction only
        </span>
      )}
    </span>
  );
}

/**
 * The C9 dead end, turned into a decision.
 *
 * A ticket lands here when the customer asked for a language no document exists
 * in. C9 (Roman 2026-08-25; confirmed by Katia and by Marcello 2026-08-26) is
 * that the machine may not substitute another language — a human decides. Until
 * P14 the human could only look: the ticket arrived in `NeedsReview` and the
 * only way to act was a terminal running `compose-drafts-once.mjs
 * --allow-language-mismatch --overridden-by <email>`.
 *
 * Red, and worded as what it actually does, because the mistake it enables is
 * shipping a safety data sheet a customer may not legally be able to read.
 * Mirrors the red block on `/approvals`, which is where the same fact reaches
 * whoever finally presses send.
 */
export function LanguageOverridePanel({
  ticket, pending, onOverride,
}: { ticket: Ticket; pending: boolean; onOverride: () => void }) {
  return (
    <div className="rounded-card border border-err-edge bg-err-50 p-4 text-sm">
      <h2 className="text-sm font-semibold text-err">No document in the requested language</h2>
      <p className="mt-1 text-body">
        The customer asked for <strong>{ticket.requestedLanguage ?? 'a language this request never stated'}</strong> and
        the search found no {ticket.requestedDocTypeCode} in it. The agent does not substitute another language — that
        decision is Regulatory's, and it is recorded with your name on it.
      </p>
      <p className="mt-2 text-xs text-muted">
        The alternative is to leave the ticket here, obtain the missing language version, and press
        <strong> Compose again</strong>.
      </p>
      <div className="mt-3">
        <Button variant="destructive" size="sm" disabled={pending} onClick={onOverride}>
          {pending ? 'Composing…' : 'Deliver another language anyway…'}
        </Button>
      </div>
    </div>
  );
}
